import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { format, parse, startOfWeek, getDay, addDays, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, Video, MapPin,
  Briefcase, Wrench, RefreshCw, Users, Filter, Phone, Mail, Clock,
  ExternalLink, Save, Trash2, Palette, Tag, Download, Printer, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { companyApi } from '../../lib/api';
import { exportPlanningPDF } from './planningExport';
import { getAssetUrl } from '../../lib/assetUrl';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const DnDCalendar = withDragAndDrop(BigCalendar);

// Slot period → time range fallback for interventions without start_time
const SLOT_TIMES = {
  morning: { start: '08:00', end: '12:00' },
  afternoon: { start: '14:00', end: '18:00' },
  full_day: { start: '08:00', end: '18:00' },
  half_day: { start: '08:00', end: '12:00' },
};

// Default event type definitions (used as fallback when no service category color)
const DEFAULT_EVENT_TYPES = {
  commercial_physical: {
    label: 'RDV physique (Commercial)',
    bg: '#7c3aed',
    border: '#6d28d9',
    icon: 'MapPin',
    owner: 'commercial',
  },
  commercial_video: {
    label: 'RDV visio (Commercial)',
    bg: '#6366f1',
    border: '#4f46e5',
    icon: 'Video',
    owner: 'commercial',
  },
  collab_punctual: {
    label: 'Intervention ponctuelle (Collaborateur)',
    bg: '#10b981',
    border: '#059669',
    icon: 'Wrench',
    owner: 'collaborator',
  },
  collab_subscription: {
    label: 'Intervention abonnement (Collaborateur)',
    bg: '#0d9488',
    border: '#0f766e',
    icon: 'Briefcase',
    owner: 'collaborator',
  },
};

const ICON_MAP = { MapPin, Video, Wrench, Briefcase };

const STORAGE_KEY = 'resawork-planning-colors';
const loadCustomColors = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const saveCustomColors = (colors) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(colors)); } catch {}
};

function getMondayOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function ymd(d) {
  return format(d, 'yyyy-MM-dd');
}

function buildDate(dateStr, timeStr) {
  // dateStr "YYYY-MM-DD", timeStr "HH:mm"
  const [y, mo, da] = dateStr.split('-').map(Number);
  const [h, mi] = (timeStr || '00:00').split(':').map(Number);
  return new Date(y, mo - 1, da, h, mi, 0);
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60000);
}

function firstName(fullName) {
  if (!fullName) return '';
  return fullName.trim().split(/\s+/)[0];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
}

function getAvatarUrl(url) {
  return getAssetUrl(url);
}

export default function UnifiedPlanning() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState(Views.DAY);
  const [isLoading, setIsLoading] = useState(true);
  const [rdvData, setRdvData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [filter, setFilter] = useState('all'); // all | commercial | collaborator | rdv | interventions
  const [collabFilter, setCollabFilter] = useState('all'); // 'all' or collaborator id
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clients, setClients] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null); // { date, start_time, end_time }
  const [qcType, setQcType] = useState('rdv'); // 'rdv' | 'intervention'
  const [qcMeetingType, setQcMeetingType] = useState('physical'); // physical | online
  const [qcClientId, setQcClientId] = useState('');
  const [qcCollabId, setQcCollabId] = useState('');
  const [qcReason, setQcReason] = useState('');
  const [qcSubmitting, setQcSubmitting] = useState(false);

  // ===== Edit event state =====
  const [editForm, setEditForm] = useState(null); // { date, start_time, end_time, collaborator_id, notes, meeting_link, meeting_type }
  const [editSaving, setEditSaving] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);

  // Service categories + services for color/label per category
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [customColors, setCustomColors] = useState(loadCustomColors());
  const [colorEditorOpen, setColorEditorOpen] = useState(false);

  // Resolve current event types config (with custom colors override)
  const EVENT_TYPES = useMemo(() => {
    const out = {};
    Object.entries(DEFAULT_EVENT_TYPES).forEach(([key, def]) => {
      const override = customColors.types?.[key];
      out[key] = {
        ...def,
        bg: override?.bg || def.bg,
        border: override?.border || def.border,
        icon: ICON_MAP[def.icon] || Briefcase,
      };
    });
    return out;
  }, [customColors]);

  // Map service_id → category info
  const serviceToCategory = useMemo(() => {
    const map = {};
    const catById = {};
    serviceCategories.forEach(c => { catById[c.id] = c; });
    services.forEach(s => {
      if (s.category_id && catById[s.category_id]) {
        map[s.id] = catById[s.category_id];
      }
    });
    return map;
  }, [services, serviceCategories]);

  // Get color/label for an event (category override OR base type)
  const resolveEventStyle = useCallback((event) => {
    const base = EVENT_TYPES[event.resource._type] || EVENT_TYPES.collab_punctual;
    const sid = event.resource.service_id || event.resource.appointment_service_id;
    const cat = sid ? serviceToCategory[sid] : null;
    // Per-category override (custom color saved in localStorage)
    const catOverride = cat ? customColors.categories?.[cat.id] : null;
    const finalBg = catOverride?.bg || cat?.color || base.bg;
    return {
      bg: finalBg,
      border: catOverride?.border || cat?.color || base.border,
      label: cat?.name || null,
      base,
    };
  }, [EVENT_TYPES, serviceToCategory, customColors]);

  // When user opens an event detail, prepare edit form
  useEffect(() => {
    if (selectedEvent) {
      const r = selectedEvent.resource;
      setEditForm({
        date: format(selectedEvent.start, 'yyyy-MM-dd'),
        start_time: format(selectedEvent.start, 'HH:mm'),
        end_time: format(selectedEvent.end, 'HH:mm'),
        collaborator_id: r._ownerId || '',
        notes: r.notes || '',
        meeting_link: r.meeting_link || '',
        meeting_type: r.meeting_type || 'physical',
      });
    } else {
      setEditForm(null);
    }
  }, [selectedEvent]);

  const saveEditedEvent = async () => {
    if (!selectedEvent || !editForm) return;
    const r = selectedEvent.resource;
    setEditSaving(true);
    try {
      if (r._kind === 'appointment' || r.booking_type === 'appointment' || r.booking_type === 'punctual') {
        const payload = {
          date: editForm.date,
          start_time: editForm.start_time,
          end_time: editForm.end_time,
          notes: editForm.notes || null,
        };
        if (editForm.collaborator_id) {
          payload.assigned_collaborator_id = editForm.collaborator_id;
        }
        if (r.booking_type === 'appointment') {
          payload.meeting_link = editForm.meeting_link || null;
          payload.meeting_type = editForm.meeting_type;
        }
        await companyApi.updatePunctualBooking(r.id, payload);
      } else {
        // Subscription booking → date + slot_period
        const startHour = parseInt(editForm.start_time.split(':')[0]);
        const newPeriod = startHour < 13 ? 'morning' : 'afternoon';
        await companyApi.rescheduleBooking(r.id, editForm.date, newPeriod);
        if (editForm.collaborator_id && editForm.collaborator_id !== r._ownerId) {
          await companyApi.assignCollaboratorToBooking(r.id, editForm.collaborator_id);
        }
      }
      toast.success('Modifications enregistrées');
      setSelectedEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!selectedEvent) return;
    const r = selectedEvent.resource;
    if (!window.confirm('Annuler cet événement ?')) return;
    setEditDeleting(true);
    try {
      if (r._kind === 'appointment' || r.booking_type === 'appointment' || r.booking_type === 'punctual') {
        await companyApi.cancelPunctualBooking(r.id);
      } else {
        await companyApi.cancelBooking(r.id);
      }
      toast.success('Événement annulé');
      setSelectedEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setEditDeleting(false);
    }
  };

  const openClientFile = () => {
    const cid = selectedEvent?.resource?.client_id || selectedEvent?.resource?.client?.id;
    if (cid) {
      navigate(`/company/clients/${cid}`);
    } else {
      toast.error('Client non trouvé');
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const weekStart = ymd(getMondayOfWeek(selectedDate));
      const [rdvRes, bookingsRes, collabRes, clientsRes, servicesRes, categoriesRes] = await Promise.all([
        companyApi.getRdvPlanning(weekStart),
        companyApi.getBookings(),
        companyApi.getCollaborators(),
        companyApi.getClients(),
        companyApi.getServices().catch(() => ({ data: [] })),
        companyApi.getServiceCategories().catch(() => ({ data: [] })),
      ]);
      setRdvData(rdvRes.data);
      setBookings(bookingsRes.data || []);
      setCollaborators(collabRes.data || []);
      setClients(clientsRes.data || []);
      setServices(servicesRes.data || []);
      setServiceCategories(categoriesRes.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const collabById = useMemo(() => {
    const map = {};
    collaborators.forEach(c => { map[c.id] = c; });
    return map;
  }, [collaborators]);

  // Build unified events list
  const events = useMemo(() => {
    const out = [];

    // 1) Appointments (RDV) from rdv-planning endpoint
    if (rdvData) {
      const allAppts = [
        ...rdvData.commercials.flatMap(c =>
          c.appointments.map(a => ({ ...a, _ownerName: c.name, _ownerRole: c.role, _ownerId: c.id }))
        ),
        ...(rdvData.unassigned || []).map(a => ({ ...a, _ownerName: 'Non assigné', _ownerRole: null, _ownerId: null })),
      ];
      allAppts.forEach(a => {
        const isVideo = a.meeting_type === 'online' || a.meeting_type === 'video';
        const typeKey = isVideo ? 'commercial_video' : 'commercial_physical';
        const startTime = a.start_time || a.time_slot || '09:00';
        const start = buildDate(a.date, startTime);
        const end = addMinutes(start, a.duration_minutes || a.duration || 60);
        const ownerObj = a._ownerId ? collabById[a._ownerId] : null;
        out.push({
          id: `appt-${a.id}`,
          title: a.client_name || 'RDV',
          start,
          end,
          resource: {
            ...a,
            _kind: 'appointment',
            _type: typeKey,
            _meetingLink: a.meeting_link,
            _ownerAvatar: ownerObj?.avatar_url || null,
          },
        });
      });
    }

    // 2) Interventions (bookings) — non-appointment, non-cancelled
    bookings.forEach(b => {
      if (b.booking_type === 'appointment') return; // already covered by rdv-planning
      if (['cancelled', 'rejected'].includes(b.status)) return;
      if (!b.date) return;

      const collab = b.assigned_collaborator_id ? collabById[b.assigned_collaborator_id] : null;
      const ownerRole = collab?.role || 'collaborator';
      const isPunctual = b.booking_type === 'punctual';
      const typeKey = isPunctual ? 'collab_punctual' : 'collab_subscription';

      // Determine times
      let startTime = b.start_time;
      let endTime = b.end_time;
      if (!startTime) {
        const fallback = SLOT_TIMES[b.slot_period] || SLOT_TIMES[b.slot_type] || SLOT_TIMES.morning;
        startTime = fallback.start;
        endTime = fallback.end;
      }
      const start = buildDate(b.date, startTime);
      const end = endTime ? buildDate(b.date, endTime) : addMinutes(start, 240);

      out.push({
        id: `booking-${b.id}`,
        title: b.client?.name || b.client_name || 'Intervention',
        start,
        end,
        resource: {
          ...b,
          _kind: 'intervention',
          _type: typeKey,
          _ownerName: collab?.name || 'Non assigné',
          _ownerRole: ownerRole,
          _ownerId: collab?.id || null,
          _ownerAvatar: collab?.avatar_url || null,
        },
      });
    });

    return out;
  }, [rdvData, bookings, collabById]);

  // Apply filters
  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (filter === 'rdv' && e.resource._kind !== 'appointment') return false;
      if (filter === 'interventions' && e.resource._kind !== 'intervention') return false;
      if (filter === 'commercial' && e.resource._ownerRole !== 'commercial') return false;
      if (filter === 'collaborator' && e.resource._ownerRole === 'commercial') return false;

      if (collabFilter !== 'all' && e.resource._ownerId !== collabFilter) return false;
      return true;
    });
  }, [events, filter, collabFilter]);

  // Stats over current view week
  const stats = useMemo(() => {
    const weekStart = getMondayOfWeek(selectedDate);
    const weekEnd = addDays(weekStart, 7);
    const inWeek = filteredEvents.filter(e => e.start >= weekStart && e.start < weekEnd);
    return {
      total: inWeek.length,
      rdv: inWeek.filter(e => e.resource._kind === 'appointment').length,
      interventions: inWeek.filter(e => e.resource._kind === 'intervention').length,
      commercial: inWeek.filter(e => e.resource._ownerRole === 'commercial').length,
    };
  }, [filteredEvents, selectedDate]);

  const eventStyleGetter = (event) => {
    const style = resolveEventStyle(event);
    return {
      style: {
        backgroundColor: style.bg,
        borderLeft: `4px solid ${style.border}`,
        borderRadius: 6,
        opacity: 0.97,
        color: '#fff',
        border: 'none',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 500,
      },
    };
  };

  const EventComponent = ({ event }) => {
    const style = resolveEventStyle(event);
    const Icon = style.base.icon;
    const owner = event.resource._ownerName;
    const avatarUrl = getAvatarUrl(event.resource._ownerAvatar);
    return (
      <div className="flex items-center gap-1.5 leading-tight overflow-hidden h-full px-0.5">
        <Icon className="w-3 h-3 flex-shrink-0 opacity-90" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="truncate font-semibold text-[12px]">{event.title}</p>
          </div>
          <div className="flex items-center gap-1">
            {style.label && (
              <span
                className="text-[9px] px-1 py-px rounded-sm font-bold uppercase tracking-tight bg-white/30 truncate max-w-[70px]"
                title={style.label}
              >
                {style.label}
              </span>
            )}
            <p className="truncate text-[10px] opacity-95 font-medium">{firstName(owner)}</p>
          </div>
        </div>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={owner}
            className="w-6 h-6 rounded-full object-cover ring-2 ring-white/80 flex-shrink-0 aspect-square"
            style={{ minWidth: '24px', minHeight: '24px' }}
          />
        ) : (
          <span
            className="w-6 h-6 rounded-full bg-white/25 text-[10px] font-bold flex items-center justify-center flex-shrink-0 ring-2 ring-white/40 aspect-square"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            {initials(owner)}
          </span>
        )}
      </div>
    );
  };

  const navigatePeriod = (dir) => {
    if (view === Views.MONTH) {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + (dir === 'next' ? 1 : -1));
      setSelectedDate(d);
    } else if (view === Views.DAY) {
      setSelectedDate(dir === 'next' ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
    } else {
      setSelectedDate(dir === 'next' ? addDays(selectedDate, 7) : subDays(selectedDate, 7));
    }
  };

  const handleExportPDF = (mode) => {
    try {
      const companyName = collaborators[0]?.company_name || '';
      const exportTypes = {};
      Object.entries(EVENT_TYPES).forEach(([key, def]) => {
        exportTypes[key] = { label: def.label, bg: def.bg };
      });
      exportPlanningPDF({
        events: filteredEvents,
        collaborators,
        companyName,
        periodLabel,
        eventTypes: exportTypes,
        resolveStyle: resolveEventStyle,
        mode,
      });
      toast.success(mode === 'all' ? 'PDF généré' : 'PDF par membre généré');
    } catch (err) {
      console.error('PDF error', err);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const periodLabel = useMemo(() => {
    if (view === Views.MONTH) return format(selectedDate, 'MMMM yyyy', { locale: fr });
    if (view === Views.DAY) return format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr });
    const ws = getMondayOfWeek(selectedDate);
    const we = addDays(ws, 6);
    return `${format(ws, 'd MMM', { locale: fr })} → ${format(we, 'd MMM yyyy', { locale: fr })}`;
  }, [selectedDate, view]);

  // ===== Drag & Drop / Resize handlers =====
  const persistMove = async (event, start, end) => {
    const r = event.resource;
    const newDate = format(start, 'yyyy-MM-dd');
    const newStart = format(start, 'HH:mm');
    const newEnd = format(end, 'HH:mm');

    try {
      if (r._kind === 'appointment' || r.booking_type === 'appointment' || r.booking_type === 'punctual') {
        // RDV (appointment) OR punctual booking → both live in punctual_bookings
        await companyApi.updatePunctualBooking(r.id, {
          date: newDate,
          start_time: newStart,
          end_time: newEnd,
        });
      } else {
        // Subscription booking → reschedule endpoint (date + slot_period)
        const startHour = start.getHours();
        const newPeriod = startHour < 13 ? 'morning' : 'afternoon';
        await companyApi.rescheduleBooking(r.id, newDate, newPeriod);
      }
      toast.success(`Déplacé au ${format(start, 'd MMM HH:mm', { locale: fr })}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du déplacement');
      fetchData(); // revert by refetching
    }
  };

  const handleEventDrop = ({ event, start, end }) => {
    // Optimistic update happens by re-rendering with refetch
    persistMove(event, start, end);
  };

  const handleEventResize = ({ event, start, end }) => {
    persistMove(event, start, end);
  };

  // ===== Quick Create on empty slot =====
  const handleSelectSlot = ({ start, end }) => {
    // If user just clicked or selected ≤ 30 min, default to 1h slot
    const isShortClick = (end.getTime() - start.getTime()) <= 30 * 60000;
    const realEnd = isShortClick ? new Date(start.getTime() + 60 * 60000) : end;
    setQuickCreate({
      date: format(start, 'yyyy-MM-dd'),
      start_time: format(start, 'HH:mm'),
      end_time: format(realEnd, 'HH:mm'),
      _start: start,
      _end: realEnd,
    });
    // Pre-select sensible defaults
    setQcType('rdv');
    setQcMeetingType('physical');
    setQcClientId('');
    // Pre-select first commercial if any
    const firstCommercial = collaborators.find(c => c.role === 'commercial');
    setQcCollabId(firstCommercial?.id || (collaborators[0]?.id || ''));
    setQcReason('');
  };

  const closeQuickCreate = () => {
    if (qcSubmitting) return;
    setQuickCreate(null);
  };

  const submitQuickCreate = async () => {
    if (!qcClientId) { toast.error('Sélectionnez un client'); return; }
    if (!qcCollabId && qcType === 'rdv') { toast.error('Sélectionnez un commercial'); return; }
    setQcSubmitting(true);
    try {
      if (qcType === 'rdv') {
        const startMin = parseInt(quickCreate.start_time.split(':')[0]) * 60 + parseInt(quickCreate.start_time.split(':')[1]);
        const endMin = parseInt(quickCreate.end_time.split(':')[0]) * 60 + parseInt(quickCreate.end_time.split(':')[1]);
        const duration = Math.max(15, endMin - startMin);
        await companyApi.createAppointmentForClient({
          client_id: qcClientId,
          date: quickCreate.date,
          time_slot: quickCreate.start_time,
          duration_minutes: duration,
          collaborator_id: qcCollabId,
          meeting_type: qcMeetingType,
          reason: qcReason || undefined,
        });
        toast.success('RDV créé');
      } else {
        // Intervention ponctuelle: half_day if < 5h, else full_day
        const startH = parseInt(quickCreate.start_time.split(':')[0]);
        const endH = parseInt(quickCreate.end_time.split(':')[0]);
        const slot_type = (endH - startH) >= 5 ? 'full_day' : 'half_day';
        await companyApi.createBookingForClient({
          client_id: qcClientId,
          date: quickCreate.date,
          slot_type,
          collaborator_id: qcCollabId || undefined,
          reason: qcReason || 'Intervention créée depuis le planning',
        });
        toast.success('Intervention créée');
      }
      setQuickCreate(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setQcSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="unified-planning-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
              <CalIcon className="w-5 h-5" />
            </span>
            Planning
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Vue unifiée des RDV commerciaux et interventions terrain · <span className="text-emerald-600 font-medium">Glissez-déposez pour reprogrammer</span> · <span className="text-blue-600 font-medium">Cliquez sur un créneau vide pour créer</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-pdf-btn">
                <Printer className="w-4 h-4 mr-2" />
                Exporter PDF
                <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportPDF('all')} data-testid="export-all">
                <Download className="w-4 h-4 mr-2" />
                Planning global
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportPDF('per-member')} data-testid="export-per-member">
                <Users className="w-4 h-4 mr-2" />
                Une page par membre
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={fetchData} data-testid="planning-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total semaine</p>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50/40 border-purple-100">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 uppercase tracking-wider flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" /> RDV Commerciaux
            </p>
            <p className="text-2xl font-bold mt-1 text-purple-700">{stats.rdv}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50/40 border-emerald-100">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
              <Wrench className="w-3 h-3" /> Interventions
            </p>
            <p className="text-2xl font-bold mt-1 text-emerald-700">{stats.interventions}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50/40 border-amber-100">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Commerciaux
            </p>
            <p className="text-2xl font-bold mt-1 text-amber-700">{stats.commercial}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & navigation */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mr-1">
              <Filter className="w-3.5 h-3.5" /> Filtre :
            </span>
            {[
              { v: 'all', label: 'Tout', icon: CalIcon },
              { v: 'rdv', label: 'RDV uniquement', icon: Briefcase },
              { v: 'interventions', label: 'Interventions uniquement', icon: Wrench },
              { v: 'commercial', label: 'Commerciaux', icon: Users },
              { v: 'collaborator', label: 'Collaborateurs', icon: Users },
            ].map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                data-testid={`filter-${v}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  filter === v
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-white border-border hover:border-foreground/30'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
            {/* Per-collaborator filter */}
            <select
              value={collabFilter}
              onChange={(e) => setCollabFilter(e.target.value)}
              data-testid="collab-filter"
              className="ml-auto h-8 text-xs border border-border rounded-md px-2 bg-white"
            >
              <option value="all">Tous les collaborateurs</option>
              {collaborators.map(c => (
                <option key={c.id} value={c.id}>
                  {c.role === 'commercial' ? 'Commercial — ' : ''}{c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigatePeriod('prev')} data-testid="nav-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())} data-testid="nav-today">
                Aujourd'hui
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigatePeriod('next')} data-testid="nav-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="ml-2 font-semibold text-base capitalize">{periodLabel}</span>
            </div>
            <div className="flex items-center gap-1 bg-muted/40 rounded-full p-0.5">
              {[
                { v: Views.DAY, label: 'Planning' },
                { v: Views.WEEK, label: 'Semaine' },
                { v: Views.MONTH, label: 'Calendrier' },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  data-testid={`view-${v}`}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    view === v ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Légende — distinguer commerciaux vs collaborateurs
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setColorEditorOpen(true)}
              data-testid="customize-colors-btn"
            >
              <Palette className="w-3.5 h-3.5 mr-1.5" />
              Personnaliser
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {Object.entries(EVENT_TYPES).map(([key, def]) => {
              const Icon = def.icon;
              return (
                <div key={key} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-muted/30 border border-border/60">
                  <span
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: def.bg, color: '#fff' }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold leading-tight truncate">{def.label}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">
                      {def.owner === 'commercial' ? 'Équipe commerciale' : 'Équipe terrain'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Service categories with custom colors */}
          {serviceCategories.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Tag className="w-3 h-3" />
                Étiquettes par catégorie de services
              </p>
              <div className="flex flex-wrap gap-2">
                {serviceCategories.map(c => {
                  const override = customColors.categories?.[c.id];
                  const color = override?.bg || c.color || '#84CC16';
                  return (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border"
                      style={{
                        backgroundColor: color + '15',
                        borderColor: color + '40',
                        color,
                      }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                      {c.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card>
        <CardContent className="p-3">
          {isLoading ? (
            <div className="h-[640px] bg-muted/40 rounded-lg animate-pulse" />
          ) : (
            <div className="rbc-resa">
              <DnDCalendar
                localizer={localizer}
                events={filteredEvents}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={setView}
                date={selectedDate}
                onNavigate={setSelectedDate}
                eventPropGetter={eventStyleGetter}
                components={{ event: EventComponent }}
                onSelectEvent={(e) => setSelectedEvent(e)}
                onSelectSlot={handleSelectSlot}
                selectable
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                resizable
                draggableAccessor={() => true}
                style={{ height: 680 }}
                step={30}
                timeslots={2}
                min={new Date(2026, 0, 1, 7, 0)}
                max={new Date(2026, 0, 1, 20, 0)}
                toolbar={false}
                culture="fr"
                messages={{
                  today: "Aujourd'hui",
                  previous: 'Précédent',
                  next: 'Suivant',
                  month: 'Mois',
                  week: 'Semaine',
                  day: 'Jour',
                  agenda: 'Agenda',
                  noEventsInRange: 'Aucun événement sur cette période',
                  allDay: 'Journée',
                  date: 'Date',
                  time: 'Heure',
                  event: 'Événement',
                }}
                formats={{
                  timeGutterFormat: (date) => format(date, 'HH:mm'),
                  eventTimeRangeFormat: ({ start, end }) =>
                    `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`,
                  dayFormat: (date) => format(date, 'EEE d', { locale: fr }),
                  dayHeaderFormat: (date) => format(date, 'EEEE d MMMM', { locale: fr }),
                  dayRangeHeaderFormat: ({ start, end }) =>
                    `${format(start, 'd MMM', { locale: fr })} - ${format(end, 'd MMM yyyy', { locale: fr })}`,
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event detail / edit dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-edit-dialog">
          {selectedEvent && editForm && (() => {
            const def = EVENT_TYPES[selectedEvent.resource._type];
            const Icon = def?.icon || CalIcon;
            const r = selectedEvent.resource;
            const isAppointment = r._kind === 'appointment' || r.booking_type === 'appointment';
            const clientId = r.client_id || r.client?.id;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: def?.bg, color: '#fff' }}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
                        {selectedEvent.title}
                        {r._ownerRole === 'commercial' && (
                          <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-[10px]">
                            Commercial
                          </Badge>
                        )}
                      </DialogTitle>
                      <DialogDescription>{def?.label}</DialogDescription>
                    </div>
                  </div>
                </DialogHeader>

                {/* Client block + access to file */}
                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm">
                        {initials(selectedEvent.title)}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{selectedEvent.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {r.client_email || r.client?.email || '—'}
                        </p>
                      </div>
                    </div>
                    {clientId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openClientFile}
                        data-testid="open-client-file-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Fiche client
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
                    {(r.client_phone || r.client?.phone) && (
                      <a href={`tel:${r.client_phone || r.client?.phone}`} className="flex items-center gap-1 hover:text-primary">
                        <Phone className="w-3 h-3" /> {r.client_phone || r.client?.phone}
                      </a>
                    )}
                    {(r.client_email || r.client?.email) && (
                      <a href={`mailto:${r.client_email || r.client?.email}`} className="flex items-center gap-1 hover:text-primary">
                        <Mail className="w-3 h-3" /> {r.client_email || r.client?.email}
                      </a>
                    )}
                  </div>
                </div>

                {/* Editable fields */}
                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-3 sm:col-span-1 space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Date</Label>
                      <Input
                        type="date"
                        value={editForm.date}
                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                        data-testid="edit-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Début</Label>
                      <Input
                        type="time"
                        value={editForm.start_time}
                        onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                        data-testid="edit-start-time"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fin</Label>
                      <Input
                        type="time"
                        value={editForm.end_time}
                        onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                        data-testid="edit-end-time"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Assigné à
                    </Label>
                    <div className="flex flex-wrap gap-2 p-2.5 bg-muted/30 rounded-lg border border-border" data-testid="edit-collab-avatars">
                      {collaborators.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">Aucun collaborateur disponible</p>
                      ) : (
                        collaborators.map(c => {
                          const isSelected = editForm.collaborator_id === c.id;
                          const avatarUrl = getAvatarUrl(c.avatar_url);
                          const isCommercial = c.role === 'commercial';
                          return (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => setEditForm({ ...editForm, collaborator_id: c.id })}
                              data-testid={`assign-${c.id}`}
                              title={`${c.name}${isCommercial ? ' · Commercial' : ''}`}
                              className={`group relative flex items-center gap-2 pr-3 pl-1 py-1 rounded-full transition-all border-2 ${
                                isSelected
                                  ? isCommercial
                                    ? 'bg-purple-50 border-purple-500 shadow-sm'
                                    : 'bg-emerald-50 border-emerald-500 shadow-sm'
                                  : 'bg-white border-transparent hover:border-border opacity-70 hover:opacity-100'
                              }`}
                            >
                              <span className="relative inline-block">
                                {avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    alt={c.name}
                                    className="w-8 h-8 rounded-full object-cover aspect-square ring-2 ring-white"
                                    style={{ minWidth: '32px', minHeight: '32px' }}
                                  />
                                ) : (
                                  <span
                                    className={`w-8 h-8 rounded-full text-white font-bold text-xs flex items-center justify-center aspect-square ring-2 ring-white ${
                                      isCommercial ? 'bg-purple-500' : 'bg-emerald-500'
                                    }`}
                                    style={{ minWidth: '32px', minHeight: '32px' }}
                                  >
                                    {initials(c.name)}
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-primary rounded-full ring-2 ring-white flex items-center justify-center">
                                    <svg viewBox="0 0 20 20" className="w-2.5 h-2.5 text-white" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12l6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                              </span>
                              <span className={`text-xs font-medium ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {firstName(c.name)}
                                {isCommercial && (
                                  <span className="text-purple-600 ml-1">★</span>
                                )}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {isAppointment && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setEditForm({ ...editForm, meeting_type: 'physical' })}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                            editForm.meeting_type === 'physical' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-border bg-white text-muted-foreground'
                          }`}
                        >
                          <MapPin className="w-4 h-4" /> Physique
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditForm({ ...editForm, meeting_type: 'online' })}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                            editForm.meeting_type === 'online' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-border bg-white text-muted-foreground'
                          }`}
                        >
                          <Video className="w-4 h-4" /> Visio
                        </button>
                      </div>
                      {editForm.meeting_type === 'online' && (
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Lien Google Meet
                          </Label>
                          <Input
                            placeholder="https://meet.google.com/..."
                            value={editForm.meeting_link}
                            onChange={(e) => setEditForm({ ...editForm, meeting_link: e.target.value })}
                            data-testid="edit-meeting-link"
                          />
                          {editForm.meeting_link && (
                            <a
                              href={editForm.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1"
                            >
                              <Video className="w-3 h-3" /> Tester le lien
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</Label>
                    <Textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={2}
                      placeholder="Commentaires internes…"
                      data-testid="edit-notes"
                    />
                  </div>
                </div>

                <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={deleteEvent}
                    disabled={editDeleting || editSaving}
                    data-testid="delete-event-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Annuler le RDV
                  </Button>
                  <div className="flex-1" />
                  <Button variant="outline" onClick={() => setSelectedEvent(null)} disabled={editSaving}>
                    Fermer
                  </Button>
                  <Button onClick={saveEditedEvent} disabled={editSaving} data-testid="save-event-btn">
                    <Save className="w-4 h-4 mr-1.5" />
                    {editSaving ? 'Enregistrement…' : 'Enregistrer'}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Color customization dialog */}
      <Dialog open={colorEditorOpen} onOpenChange={setColorEditorOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="color-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              Personnaliser les couleurs
            </DialogTitle>
            <DialogDescription>
              Définissez les couleurs de la légende et de chaque catégorie de services
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Legend type colors */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Types principaux</p>
              {Object.entries(DEFAULT_EVENT_TYPES).map(([key, def]) => {
                const current = customColors.types?.[key]?.bg || def.bg;
                const Icon = ICON_MAP[def.icon] || Briefcase;
                return (
                  <div key={key} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-muted/20">
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: current, color: '#fff' }}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <p className="flex-1 text-sm font-medium">{def.label}</p>
                    <input
                      type="color"
                      value={current}
                      onChange={(e) => {
                        const next = {
                          ...customColors,
                          types: {
                            ...(customColors.types || {}),
                            [key]: { bg: e.target.value, border: e.target.value },
                          },
                        };
                        setCustomColors(next);
                        saveCustomColors(next);
                      }}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                      data-testid={`color-type-${key}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Service categories colors */}
            {serviceCategories.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Tag className="w-3 h-3" />
                  Catégories de services
                </p>
                {serviceCategories.map(c => {
                  const override = customColors.categories?.[c.id];
                  const color = override?.bg || c.color || '#84CC16';
                  return (
                    <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-muted/20">
                      <span
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        <Tag className="w-3.5 h-3.5 text-white" />
                      </span>
                      <p className="flex-1 text-sm font-medium">{c.name}</p>
                      <input
                        type="color"
                        value={color}
                        onChange={async (e) => {
                          const newColor = e.target.value;
                          // Save locally for instant feedback
                          const next = {
                            ...customColors,
                            categories: {
                              ...(customColors.categories || {}),
                              [c.id]: { bg: newColor, border: newColor },
                            },
                          };
                          setCustomColors(next);
                          saveCustomColors(next);
                          // Also persist to backend so other users see it
                          try {
                            await companyApi.updateServiceCategory(c.id, { color: newColor });
                            setServiceCategories(prev => prev.map(x => x.id === c.id ? { ...x, color: newColor } : x));
                          } catch {}
                        }}
                        className="w-10 h-8 rounded cursor-pointer border border-border"
                        data-testid={`color-cat-${c.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {serviceCategories.length === 0 && (
              <p className="text-sm text-muted-foreground italic text-center py-4">
                Créez d'abord des catégories de services depuis la page Services
                pour leur attribuer des couleurs personnalisées.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCustomColors({});
                saveCustomColors({});
                toast.success('Couleurs réinitialisées');
              }}
              data-testid="reset-colors-btn"
            >
              Réinitialiser
            </Button>
            <Button onClick={() => setColorEditorOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Create dialog */}
      <Dialog open={!!quickCreate} onOpenChange={(o) => !o && closeQuickCreate()}>
        <DialogContent className="sm:max-w-md" data-testid="quick-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalIcon className="w-5 h-5 text-primary" />
              Créer rapidement
            </DialogTitle>
            <DialogDescription>
              {quickCreate && (
                <>
                  {format(quickCreate._start, 'EEEE d MMMM', { locale: fr })} ·{' '}
                  <span className="font-medium text-foreground">{quickCreate.start_time} → {quickCreate.end_time}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setQcType('rdv')}
                data-testid="qc-type-rdv"
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  qcType === 'rdv' ? 'border-purple-500 bg-purple-50' : 'border-border bg-white hover:border-purple-200'
                }`}
              >
                <Briefcase className={`w-5 h-5 mb-1 ${qcType === 'rdv' ? 'text-purple-600' : 'text-muted-foreground'}`} />
                <p className="font-semibold text-sm">RDV Commercial</p>
                <p className="text-[11px] text-muted-foreground">Visio ou physique</p>
              </button>
              <button
                type="button"
                onClick={() => setQcType('intervention')}
                data-testid="qc-type-intervention"
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  qcType === 'intervention' ? 'border-emerald-500 bg-emerald-50' : 'border-border bg-white hover:border-emerald-200'
                }`}
              >
                <Wrench className={`w-5 h-5 mb-1 ${qcType === 'intervention' ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                <p className="font-semibold text-sm">Intervention</p>
                <p className="text-[11px] text-muted-foreground">Ponctuelle terrain</p>
              </button>
            </div>

            {/* Meeting type for RDV only */}
            {qcType === 'rdv' && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setQcMeetingType('physical')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                    qcMeetingType === 'physical' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-border bg-white text-muted-foreground'
                  }`}
                >
                  <MapPin className="w-4 h-4" /> Physique
                </button>
                <button
                  type="button"
                  onClick={() => setQcMeetingType('online')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                    qcMeetingType === 'online' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-border bg-white text-muted-foreground'
                  }`}
                >
                  <Video className="w-4 h-4" /> Visio
                </button>
              </div>
            )}

            {/* Client */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client</Label>
              <Select value={qcClientId} onValueChange={setQcClientId}>
                <SelectTrigger data-testid="qc-client-select">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.email ? `· ${c.email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Collaborator */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {qcType === 'rdv' ? 'Commercial' : 'Collaborateur'} {qcType === 'rdv' && <span className="text-red-500">*</span>}
              </Label>
              <Select value={qcCollabId} onValueChange={setQcCollabId}>
                <SelectTrigger data-testid="qc-collab-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {collaborators
                    .filter(c => qcType !== 'rdv' || c.role === 'commercial' || true)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.role === 'commercial' ? '★ ' : ''}{c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reason / Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Motif (optionnel)</Label>
              <Textarea
                value={qcReason}
                onChange={(e) => setQcReason(e.target.value)}
                placeholder="Ex: Devis taille de haies, première visite…"
                rows={2}
                data-testid="qc-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeQuickCreate} disabled={qcSubmitting}>Annuler</Button>
            <Button onClick={submitQuickCreate} disabled={qcSubmitting} data-testid="qc-submit">
              {qcSubmitting ? 'Création…' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom CSS for big-calendar look in our enterprise theme */}
      <style>{`
        .rbc-resa .rbc-calendar { font-family: inherit; }
        .rbc-resa .rbc-time-view, .rbc-resa .rbc-month-view {
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }
        .rbc-resa .rbc-time-header-content,
        .rbc-resa .rbc-time-content,
        .rbc-resa .rbc-day-bg + .rbc-day-bg,
        .rbc-resa .rbc-month-row + .rbc-month-row,
        .rbc-resa .rbc-header,
        .rbc-resa .rbc-time-header,
        .rbc-resa .rbc-time-gutter,
        .rbc-resa .rbc-time-content > * + * > * {
          border-color: hsl(var(--border)) !important;
        }
        .rbc-resa .rbc-header {
          background: hsl(220 14% 96%);
          padding: 8px 6px;
          font-weight: 600;
          font-size: 12px;
          color: hsl(var(--foreground));
          text-transform: capitalize;
        }
        .rbc-resa .rbc-today { background: hsl(var(--primary) / 0.06); }
        .rbc-resa .rbc-time-gutter { font-size: 11px; color: hsl(var(--muted-foreground)); }
        .rbc-resa .rbc-current-time-indicator { background-color: hsl(var(--primary)); height: 2px; }
        .rbc-resa .rbc-event { box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
        .rbc-resa .rbc-event:focus { outline: 2px solid hsl(var(--ring)); outline-offset: 1px; }
        .rbc-resa .rbc-event { cursor: grab; }
        .rbc-resa .rbc-event:active { cursor: grabbing; }
        .rbc-resa .rbc-addons-dnd-drag-preview { opacity: 0.7; }
        .rbc-resa .rbc-addons-dnd-resize-ns-icon,
        .rbc-resa .rbc-addons-dnd-resize-ew-icon { background-color: rgba(255,255,255,0.85); }
        .rbc-resa .rbc-show-more { color: hsl(var(--primary)); font-weight: 500; }
      `}</style>
    </div>
  );
}
